/**
 * Budget — modulo Presupuesto de la organizacion.
 *
 * Conectada a /api/management/budgets (F3). El submodulo se activa por org
 * via `config.management.budget.enabled`. Las cantidades se almacenan en
 * CENTIMOS en BD; este componente las muestra en EUROS dividiendo por 100.
 *
 * Tabs:
 *   - Resumen     KPIs globales con toggle Actual/Proyeccion.
 *   - Partidas    Lista de BudgetLine con plan vs ejecutado.
 *   - Apuntes     BudgetEntry paginados con filtros + alta.
 *   - Ejercicios  Lista de Budget con cambio de estado (aprobar/cerrar).
 *
 * Decisiones en DOC/ES/MODULO_PRESUPUESTO_DECISIONES.md.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import {
  budgetsApi,
  type Budget,
  type BudgetLine,
  type BudgetEntry,
  type BudgetSummary,
  type BudgetEntryStatus,
  type BudgetLineKind,
  type BudgetEntryMethod,
} from '@/services/api';
import { useManagementFeature } from '@/hooks/useManagementFeature';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import toast from 'react-hot-toast';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatEur(centimos: number): string {
  return (centimos / 100).toLocaleString('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

function formatEur2(centimos: number): string {
  return (centimos / 100).toLocaleString('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  });
}

// CSV: escapa campos con comas/comillas/saltos de linea (RFC 4180).
function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  if (s === '') return '';
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename: string, rows: string[][]): void {
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  // BOM para que Excel detecte UTF-8 correctamente.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: Budget['status'] }) {
  const map: Record<Budget['status'], { bg: string; label: string }> = {
    draft: { bg: 'bg-gray-100 text-gray-700', label: 'Borrador' },
    approved: { bg: 'bg-emerald-100 text-emerald-700', label: 'Aprobado' },
    closed: { bg: 'bg-slate-200 text-slate-700', label: 'Cerrado' },
  };
  const m = map[status];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${m.bg}`}>{m.label}</span>;
}

function ProgressBar({
  value,
  max,
  accent = 'vida',
}: {
  value: number;
  max: number;
  accent?: 'vida' | 'emerald' | 'amber' | 'red';
}) {
  const pct = Math.min(100, max > 0 ? Math.round((value / max) * 100) : 0);
  const bg: Record<string, string> = {
    vida: 'bg-vida-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };
  return (
    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full ${bg[accent]} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pantalla deshabilitada
// -----------------------------------------------------------------------------

function ModuleDisabled() {
  return (
    <div>
      <Header title="Presupuesto" subtitle="Gestión económica de la organización" />
      <div className="p-6">
        <div className="max-w-xl mx-auto rounded-xl border border-gray-200 bg-white p-8 text-center">
          <NoSymbolIcon className="h-12 w-12 mx-auto text-gray-300" />
          <h2 className="mt-3 text-base font-semibold text-gray-900">
            Módulo Presupuesto no habilitado
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Esta organización no tiene activado el módulo de presupuesto. Un
            administrador puede habilitarlo desde la configuración
            (<code className="text-xs">config.management.budget.enabled</code>).
          </p>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Componente principal
// -----------------------------------------------------------------------------

type Tab = 'summary' | 'lines' | 'entries' | 'exercises';
type ViewMode = 'actual' | 'projection';

export default function BudgetPage() {
  const enabled = useManagementFeature('budget');
  if (!enabled) return <ModuleDisabled />;
  return <BudgetInner />;
}

function BudgetInner() {
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission(PERMISSIONS.BUDGET_MANAGE);
  const canApprove = hasPermission(PERMISSIONS.BUDGET_APPROVE);
  // Transparencia: en modo aggregated_only, quien no tiene MANAGE/APPROVE no
  // accede a apuntes individuales — solo ve Resumen/Partidas/Ejercicios. El
  // backend tambien bloquea el listado de entries con 403, pero ocultar la
  // pestaña evita el error visible al usuario.
  const selectedOrg = useOrganizationStore((s) => s.selectedOrg);
  const transparency =
    selectedOrg?.config?.management?.budget?.transparency ?? 'full';
  const canSeeEntries = canManage || canApprove || transparency === 'full';

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [selected, setSelected] = useState<Budget | null>(null);
  const [tab, setTab] = useState<Tab>('summary');
  const [view, setView] = useState<ViewMode>('actual');
  const [loading, setLoading] = useState(true);
  const [creatingBudget, setCreatingBudget] = useState(false);

  const loadBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await budgetsApi.list();
      setBudgets(data.budgets);
      // Preferimos el aprobado; si no hay, el primero (mas reciente por sort).
      const current =
        data.budgets.find((b) => b.status === 'approved') || data.budgets[0] || null;
      setSelected((cur) => cur || current);
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.error || 'Error cargando ejercicios');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  return (
    <div>
      <Header
        title="Presupuesto"
        subtitle={
          selected ? `Ejercicio ${selected.year}` : 'Gestión económica de la organización'
        }
        actions={
          <div className="flex items-center gap-2">
            {budgets.length > 0 && selected && (
              <select
                value={selected._id}
                onChange={(e) => {
                  const b = budgets.find((x) => x._id === e.target.value);
                  if (b) setSelected(b);
                }}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                {budgets.map((b) => (
                  <option key={b._id} value={b._id}>
                    Ejercicio {b.year} ({b.status})
                  </option>
                ))}
              </select>
            )}
            {canManage && (
              <button
                onClick={() => setCreatingBudget(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
              >
                <PlusIcon className="h-4 w-4" /> Nuevo ejercicio
              </button>
            )}
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {loading && <p className="text-sm text-gray-500">Cargando…</p>}

        {!loading && budgets.length === 0 && (
          <EmptyState onCreate={canManage ? () => setCreatingBudget(true) : undefined} />
        )}

        {!loading && selected && (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200 -mb-px flex items-center gap-4">
              {(['summary', 'lines', ...(canSeeEntries ? (['entries'] as const) : []), 'exercises'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                    tab === t
                      ? 'border-vida-600 text-vida-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'summary' && 'Resumen'}
                  {t === 'lines' && 'Partidas'}
                  {t === 'entries' && 'Apuntes'}
                  {t === 'exercises' && 'Ejercicios'}
                </button>
              ))}
              {(tab === 'summary' || tab === 'lines') && (
                <div className="ml-auto inline-flex bg-gray-100 rounded-lg p-0.5">
                  {(['actual', 'projection'] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setView(m)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                        view === m ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                      }`}
                    >
                      {m === 'actual' ? 'Actual' : 'Proyección'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {tab === 'summary' && <SummaryTab budget={selected} view={view} />}
            {tab === 'lines' && (
              <LinesTab budget={selected} view={view} canManage={canManage} />
            )}
            {tab === 'entries' && <EntriesTab budget={selected} canManage={canManage} />}
            {tab === 'exercises' && (
              <ExercisesTab
                budgets={budgets}
                canManage={canManage}
                canApprove={canApprove}
                onChanged={loadBudgets}
              />
            )}
          </>
        )}
      </div>

      {creatingBudget && (
        <NewBudgetForm onClose={() => setCreatingBudget(false)} onSaved={loadBudgets} />
      )}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <div className="max-w-xl mx-auto rounded-xl border border-gray-200 bg-white p-8 text-center">
      <h2 className="text-base font-semibold text-gray-900">Sin ejercicios aún</h2>
      <p className="mt-2 text-sm text-gray-500">
        Crea el primer ejercicio anual para empezar a presupuestar partidas y registrar apuntes.
      </p>
      {onCreate && (
        <button
          onClick={onCreate}
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
        >
          <PlusIcon className="h-4 w-4" /> Crear primer ejercicio
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab: Resumen
// -----------------------------------------------------------------------------

function SummaryTab({ budget, view }: { budget: Budget; view: ViewMode }) {
  const [data, setData] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    budgetsApi
      .summary(budget._id)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [budget._id]);

  if (loading) return <p className="text-sm text-gray-500">Cargando resumen…</p>;
  if (!data) return <p className="text-sm text-gray-500">Sin datos.</p>;

  // Actual: usa paid. Proyeccion: usa total (paid + pending).
  const income = view === 'actual' ? data.totals.income.paid : data.totals.income.total;
  const expense = view === 'actual' ? data.totals.expense.paid : data.totals.expense.total;
  const net = view === 'actual' ? data.net.paid : data.net.projected;
  const incPlanned = data.totals.income.planned;
  const expPlanned = data.totals.expense.planned;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiCard label="Ingresos" value={income} planned={incPlanned} accent="emerald" />
        <KpiCard label="Gastos" value={expense} planned={expPlanned} accent="red" />
        <KpiCard label="Neto" value={net} accent={net >= 0 ? 'emerald' : 'red'} />
        <KpiCard label="Plan vs " value={data.net.planned} accent="vida" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Desglose por partida</h3>
        <SummaryLinesTable lines={data.lines} view={view} />
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  planned,
  accent,
}: {
  label: string;
  value: number;
  planned?: number;
  accent: 'emerald' | 'red' | 'vida';
}) {
  const colorClass: Record<string, string> = {
    emerald: 'text-emerald-700',
    red: 'text-red-700',
    vida: 'text-vida-700',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${colorClass[accent]}`}>{formatEur(value)}</p>
      {planned !== undefined && (
        <p className="mt-0.5 text-xs text-gray-500">Plan {formatEur(planned)}</p>
      )}
    </div>
  );
}

function SummaryLinesTable({
  lines,
  view,
}: {
  lines: BudgetSummary['lines'];
  view: ViewMode;
}) {
  const grouped = useMemo(() => {
    const byKind: Record<BudgetLineKind, typeof lines> = { income: [], expense: [] };
    for (const l of lines) byKind[l.kind].push(l);
    return byKind;
  }, [lines]);

  return (
    <div className="space-y-5">
      {(['income', 'expense'] as const).map((k) => (
        <div key={k}>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
            {k === 'income' ? (
              <ArrowDownLeftIcon className="h-4 w-4 text-emerald-600" />
            ) : (
              <ArrowUpRightIcon className="h-4 w-4 text-red-600" />
            )}
            {k === 'income' ? 'Ingresos' : 'Gastos'}
          </p>
          {grouped[k].length === 0 ? (
            <p className="text-xs text-gray-400">Sin partidas de este tipo.</p>
          ) : (
            <ul className="space-y-2">
              {grouped[k].map((l) => {
                const exec = view === 'actual' ? l.executed.paid : l.executed.total;
                const pctOfPlan =
                  l.plannedAmount > 0 ? Math.round((exec / l.plannedAmount) * 100) : 0;
                return (
                  <li
                    key={l._id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <p className="text-sm font-medium text-gray-900">{l.label}</p>
                      <p className="text-sm font-semibold text-gray-700">
                        {formatEur(exec)}{' '}
                        <span className="text-xs font-normal text-gray-500">
                          / {formatEur(l.plannedAmount)}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <ProgressBar
                        value={exec}
                        max={l.plannedAmount}
                        accent={k === 'income' ? 'emerald' : 'red'}
                      />
                      <span className="text-xs text-gray-500 w-12 text-right">{pctOfPlan}%</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Categoría {l.category} · {l.executed.entriesCount} apunte
                      {l.executed.entriesCount === 1 ? '' : 's'}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab: Partidas (CRUD)
// -----------------------------------------------------------------------------

function LinesTab({
  budget,
  view,
  canManage,
}: {
  budget: Budget;
  view: ViewMode;
  canManage: boolean;
}) {
  const [data, setData] = useState<BudgetSummary | null>(null);
  const [editing, setEditing] = useState<BudgetLine | 'new' | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await budgetsApi.summary(budget._id);
      setData(data);
    } catch {
      // already toasted in summary if needed
    } finally {
      setLoading(false);
    }
  }, [budget._id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onDelete = async (l: BudgetLine) => {
    if (!window.confirm(`¿Eliminar la partida "${l.label}"?`)) return;
    try {
      await budgetsApi.removeLine(budget._id, l._id);
      toast.success('Partida eliminada');
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Cargando partidas…</p>;

  return (
    <div className="space-y-4">
      {canManage && (
        <button
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <PlusIcon className="h-4 w-4" /> Nueva partida
        </button>
      )}

      {data && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Partida</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Categoría</th>
                <th className="text-right px-4 py-2">Plan</th>
                <th className="text-right px-4 py-2">
                  {view === 'actual' ? 'Cobrado/Pagado' : 'Proyección'}
                </th>
                <th className="text-left px-4 py-2 w-40">Avance</th>
                {canManage && <th className="text-right px-4 py-2 w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.lines.length === 0 && (
                <tr>
                  <td
                    colSpan={canManage ? 6 : 5}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    Aún no hay partidas en este ejercicio.
                  </td>
                </tr>
              )}
              {data.lines.map((l) => {
                const exec = view === 'actual' ? l.executed.paid : l.executed.total;
                const isIncome = l.kind === 'income';
                return (
                  <tr key={l._id}>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      <span
                        className={`inline-block w-1 h-4 align-middle mr-2 rounded ${
                          isIncome ? 'bg-emerald-500' : 'bg-red-500'
                        }`}
                      />
                      {l.label}
                    </td>
                    <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{l.category}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-gray-700">
                      {formatEur(l.plannedAmount)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs font-semibold text-gray-900">
                      {formatEur(exec)}
                    </td>
                    <td className="px-4 py-2">
                      <ProgressBar
                        value={exec}
                        max={l.plannedAmount}
                        accent={isIncome ? 'emerald' : 'red'}
                      />
                    </td>
                    {canManage && (
                      <td className="px-4 py-2 text-right space-x-2">
                        <button
                          onClick={() =>
                            setEditing({
                              _id: l._id,
                              budgetId: budget._id,
                              organization_id: budget.organization_id,
                              kind: l.kind,
                              category: l.category,
                              label: l.label,
                              plannedAmount: l.plannedAmount,
                              notes: l.notes,
                              order: l.order,
                              created_at: '',
                              updated_at: '',
                            })
                          }
                          className="text-gray-400 hover:text-vida-700"
                        >
                          <PencilSquareIcon className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() =>
                            onDelete({
                              _id: l._id,
                              budgetId: budget._id,
                              organization_id: budget.organization_id,
                              kind: l.kind,
                              category: l.category,
                              label: l.label,
                              plannedAmount: l.plannedAmount,
                              notes: l.notes,
                              order: l.order,
                              created_at: '',
                              updated_at: '',
                            })
                          }
                          className="text-gray-400 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4 inline" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <LineEditor
          budget={budget}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function LineEditor({
  budget,
  initial,
  onClose,
  onSaved,
}: {
  budget: Budget;
  initial: BudgetLine | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    kind: initial?.kind || ('income' as BudgetLineKind),
    category: initial?.category || '',
    label: initial?.label || '',
    plannedEur: initial ? (initial.plannedAmount / 100).toString() : '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const plannedEur = parseFloat(form.plannedEur.replace(',', '.'));
    if (!form.label.trim() || !form.category.trim()) {
      toast.error('Categoría y etiqueta son obligatorias');
      return;
    }
    if (Number.isNaN(plannedEur) || plannedEur < 0) {
      toast.error('Importe planeado inválido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        category: form.category.trim(),
        label: form.label.trim(),
        plannedAmount: Math.round(plannedEur * 100),
        notes: form.notes || undefined,
      };
      if (isEdit && initial) {
        await budgetsApi.updateLine(budget._id, initial._id, payload);
        toast.success('Partida actualizada');
      } else {
        await budgetsApi.createLine(budget._id, payload);
        toast.success('Partida creada');
      }
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel title={isEdit ? 'Editar partida' : 'Nueva partida'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col flex-1">
        <div className="p-4 space-y-3 flex-1">
          <Field label="Tipo" required>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as BudgetLineKind })}
              className="form-input"
            >
              <option value="income">Ingreso</option>
              <option value="expense">Gasto</option>
            </select>
          </Field>
          <Field label="Categoría" required>
            <input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="form-input"
              placeholder="Cuotas, Estructura, Subvenciones…"
              required
            />
          </Field>
          <Field label="Etiqueta" required>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="form-input"
              required
            />
          </Field>
          <Field label="Importe planeado (€)" required>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.plannedEur}
              onChange={(e) => setForm({ ...form, plannedEur: e.target.value })}
              className="form-input"
              required
            />
          </Field>
          <Field label="Notas">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="form-input"
            />
          </Field>
        </div>
        <PanelFooter onCancel={onClose} saving={saving} saveLabel={isEdit ? 'Guardar' : 'Crear'} />
      </form>
    </SidePanel>
  );
}

// -----------------------------------------------------------------------------
// Tab: Apuntes (CRUD + filtros)
// -----------------------------------------------------------------------------

function EntriesTab({ budget, canManage }: { budget: Budget; canManage: boolean }) {
  const [entries, setEntries] = useState<BudgetEntry[]>([]);
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BudgetEntryStatus | 'all'>('all');
  const [kindFilter, setKindFilter] = useState<BudgetLineKind | 'all'>('all');
  const [editing, setEditing] = useState<BudgetEntry | 'new' | null>(null);

  const lineMap = useMemo(() => {
    const m = new Map<string, BudgetLine>();
    for (const l of lines) m.set(l._id, l);
    return m;
  }, [lines]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof budgetsApi.entries>[1] = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (kindFilter !== 'all') params.kind = kindFilter;
      const [eRes, lRes] = await Promise.all([
        budgetsApi.entries(budget._id, params),
        budgetsApi.lines(budget._id),
      ]);
      setEntries(eRes.data.entries);
      setLines(lRes.data.lines);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Error cargando apuntes');
    } finally {
      setLoading(false);
    }
  }, [budget._id, statusFilter, kindFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onDelete = async (entry: BudgetEntry) => {
    if (!window.confirm(`¿Eliminar el apunte "${entry.label}" (${formatEur2(entry.amount)})?`))
      return;
    try {
      await budgetsApi.removeEntry(budget._id, entry._id);
      toast.success('Apunte eliminado');
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'income', 'expense'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setKindFilter(k)}
            className={`px-3 py-1 text-xs rounded-full border ${
              kindFilter === k
                ? 'border-vida-600 bg-vida-50 text-vida-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {k === 'all' ? 'Todos' : k === 'income' ? 'Ingresos' : 'Gastos'}
          </button>
        ))}
        <div className="w-px h-5 bg-gray-200" />
        {(['all', 'paid', 'pending'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full border ${
              statusFilter === s
                ? 'border-vida-600 bg-vida-50 text-vida-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'Cualquier estado' : s === 'paid' ? 'Cobrado/Pagado' : 'Pendiente'}
          </button>
        ))}
        {entries.length > 0 && (
          <button
            onClick={() => {
              const rows: string[][] = [
                [
                  'Fecha',
                  'Tipo',
                  'Estado',
                  'Partida',
                  'Concepto',
                  'Beneficiario',
                  'Importe (EUR)',
                  'Metodo',
                  'Referencia',
                ],
                ...entries.map((e) => [
                  e.date.slice(0, 10),
                  e.kind === 'income' ? 'Ingreso' : 'Gasto',
                  e.status === 'paid' ? 'Realizado' : 'Pendiente',
                  lineMap.get(e.budgetLineId)?.label || '',
                  e.label,
                  e.payee,
                  (e.amount / 100).toFixed(2),
                  e.method,
                  e.reference || '',
                ]),
              ];
              downloadCsv(`presupuesto-${budget.year}-apuntes.csv`, rows);
            }}
            className={`${canManage && lines.length > 0 ? '' : 'ml-auto'} inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50`}
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> Exportar CSV
          </button>
        )}
        {canManage && lines.length > 0 && (
          <button
            onClick={() => setEditing('new')}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-vida-600 text-white rounded-lg hover:bg-vida-700"
          >
            <PlusIcon className="h-4 w-4" /> Nuevo apunte
          </button>
        )}
      </div>

      {loading && <p className="text-sm text-gray-500">Cargando…</p>}

      {!loading && entries.length === 0 && (
        <p className="text-sm text-gray-500">
          {lines.length === 0
            ? 'Crea primero alguna partida para poder dar de alta apuntes.'
            : 'Sin apuntes para los filtros actuales.'}
        </p>
      )}

      {!loading && entries.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Fecha</th>
                <th className="text-left px-4 py-2">Concepto</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Partida</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Beneficiario</th>
                <th className="text-right px-4 py-2">Importe</th>
                <th className="text-left px-4 py-2">Estado</th>
                {canManage && <th className="text-right px-4 py-2 w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((e) => {
                const line = lineMap.get(e.budgetLineId);
                return (
                  <tr key={e._id}>
                    <td className="px-4 py-2 text-gray-600">{formatDate(e.date)}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{e.label}</td>
                    <td className="px-4 py-2 text-gray-600 hidden md:table-cell">
                      {line ? line.label : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{e.payee}</td>
                    <td
                      className={`px-4 py-2 text-right font-mono text-xs font-semibold ${
                        e.kind === 'income' ? 'text-emerald-700' : 'text-red-700'
                      }`}
                    >
                      {e.kind === 'income' ? '+' : '−'} {formatEur2(e.amount)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          e.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {e.status === 'paid' ? 'Realizado' : 'Pendiente'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-4 py-2 text-right space-x-2">
                        <button
                          onClick={() => setEditing(e)}
                          className="text-gray-400 hover:text-vida-700"
                        >
                          <PencilSquareIcon className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => onDelete(e)}
                          className="text-gray-400 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4 inline" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <EntryEditor
          budget={budget}
          lines={lines}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function EntryEditor({
  budget,
  lines,
  initial,
  onClose,
  onSaved,
}: {
  budget: Budget;
  lines: BudgetLine[];
  initial: BudgetEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    budgetLineId: initial?.budgetLineId || lines[0]?._id || '',
    kind: initial?.kind || lines[0]?.kind || ('income' as BudgetLineKind),
    status: initial?.status || ('pending' as BudgetEntryStatus),
    date: initial?.date ? initial.date.slice(0, 10) : today,
    amountEur: initial ? (initial.amount / 100).toString() : '',
    label: initial?.label || '',
    payee: initial?.payee || '',
    method: initial?.method || ('transferencia' as BudgetEntryMethod),
    reference: initial?.reference || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  // Cuando cambias la partida, sincronizamos kind para evitar inconsistencia.
  useEffect(() => {
    const line = lines.find((l) => l._id === form.budgetLineId);
    if (line && line.kind !== form.kind) {
      setForm((f) => ({ ...f, kind: line.kind }));
    }
  }, [form.budgetLineId, lines, form.kind]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.label.trim() || !form.payee.trim()) {
      toast.error('Concepto y beneficiario son obligatorios');
      return;
    }
    const eur = parseFloat(form.amountEur.replace(',', '.'));
    if (Number.isNaN(eur) || eur < 0) {
      toast.error('Importe inválido');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<BudgetEntry> = {
        budgetLineId: form.budgetLineId,
        kind: form.kind,
        status: form.status,
        date: form.date,
        amount: Math.round(eur * 100),
        label: form.label.trim(),
        payee: form.payee.trim(),
        method: form.method,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
      };
      if (isEdit && initial) {
        // budgetLineId no se actualiza desde aqui (es required para crear).
        // Si el usuario lo cambia, el backend ignora el campo (omit en
        // entryUpdateSchema). Aceptable porque la UI no permite mover apuntes
        // entre partidas hoy.
        await budgetsApi.updateEntry(budget._id, initial._id, payload);
        toast.success('Apunte actualizado');
      } else {
        await budgetsApi.createEntry(budget._id, payload);
        toast.success('Apunte creado');
      }
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel title={isEdit ? 'Editar apunte' : 'Nuevo apunte'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col flex-1">
        <div className="p-4 space-y-3 flex-1">
          <Field label="Partida" required>
            <select
              value={form.budgetLineId}
              onChange={(e) => setForm({ ...form, budgetLineId: e.target.value })}
              className="form-input"
              required
            >
              {lines.map((l) => (
                <option key={l._id} value={l._id}>
                  [{l.kind === 'income' ? 'Ing' : 'Gasto'}] {l.label}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha" required>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="form-input"
                required
              />
            </Field>
            <Field label="Importe (€)" required>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amountEur}
                onChange={(e) => setForm({ ...form, amountEur: e.target.value })}
                className="form-input"
                required
              />
            </Field>
          </div>
          <Field label="Concepto" required>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="form-input"
              required
            />
          </Field>
          <Field label="Beneficiario / Pagador" required>
            <input
              value={form.payee}
              onChange={(e) => setForm({ ...form, payee: e.target.value })}
              className="form-input"
              required
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Método">
              <select
                value={form.method}
                onChange={(e) =>
                  setForm({ ...form, method: e.target.value as BudgetEntryMethod })
                }
                className="form-input"
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="bizum">Bizum</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="domiciliado">Domiciliado</option>
                <option value="otro">Otro</option>
              </select>
            </Field>
            <Field label="Estado">
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as BudgetEntryStatus })
                }
                className="form-input"
              >
                <option value="pending">Pendiente</option>
                <option value="paid">Cobrado / Pagado</option>
              </select>
            </Field>
          </div>
          <Field label="Referencia">
            <input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="form-input"
              placeholder="Factura nº, contrato…"
            />
          </Field>
          <Field label="Notas">
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="form-input"
            />
          </Field>
        </div>
        <PanelFooter onCancel={onClose} saving={saving} saveLabel={isEdit ? 'Guardar' : 'Crear'} />
      </form>
    </SidePanel>
  );
}

// -----------------------------------------------------------------------------
// Tab: Ejercicios (lista + cambio de estado)
// -----------------------------------------------------------------------------

function ExercisesTab({
  budgets,
  canManage,
  canApprove,
  onChanged,
}: {
  budgets: Budget[];
  canManage: boolean;
  canApprove: boolean;
  onChanged: () => void;
}) {
  const changeStatus = async (b: Budget, status: Budget['status']) => {
    try {
      await budgetsApi.update(b._id, { status });
      toast.success(
        status === 'approved' ? 'Ejercicio aprobado' : status === 'closed' ? 'Ejercicio cerrado' : 'Estado actualizado'
      );
      onChanged();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Año</th>
            <th className="text-left px-4 py-2">Estado</th>
            <th className="text-left px-4 py-2 hidden md:table-cell">Aprobado</th>
            <th className="text-left px-4 py-2 hidden md:table-cell">Cerrado</th>
            {canManage && <th className="text-right px-4 py-2">Acciones</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {budgets.map((b) => (
            <tr key={b._id}>
              <td className="px-4 py-2 font-medium text-gray-900">{b.year}</td>
              <td className="px-4 py-2">
                <StatusBadge status={b.status} />
              </td>
              <td className="px-4 py-2 text-gray-600 hidden md:table-cell">
                {formatDate(b.approvedAt)}
              </td>
              <td className="px-4 py-2 text-gray-600 hidden md:table-cell">
                {formatDate(b.closedAt)}
              </td>
              {canManage && (
                <td className="px-4 py-2 text-right space-x-2">
                  {b.status === 'draft' && canApprove && (
                    <button
                      onClick={() => changeStatus(b, 'approved')}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100"
                    >
                      <CheckCircleIcon className="h-3.5 w-3.5" /> Aprobar
                    </button>
                  )}
                  {b.status === 'approved' && canApprove && (
                    <button
                      onClick={() => changeStatus(b, 'closed')}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-200"
                    >
                      <LockClosedIcon className="h-3.5 w-3.5" /> Cerrar
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewBudgetForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await budgetsApi.create({ year, notes: notes || undefined });
      toast.success(`Ejercicio ${year} creado`);
      onSaved();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel title="Nuevo ejercicio" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col flex-1">
        <div className="p-4 space-y-3 flex-1">
          <Field label="Año" required>
            <input
              type="number"
              min={2000}
              max={2200}
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10) || currentYear)}
              className="form-input"
              required
            />
          </Field>
          <Field label="Notas">
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-input"
            />
          </Field>
          <p className="text-xs text-gray-500">
            El ejercicio se crea en estado <code>borrador</code>. Cuando esté listo se aprueba
            desde la pestaña Ejercicios (requiere permiso <code>budget:approve</code>).
          </p>
        </div>
        <PanelFooter onCancel={onClose} saving={saving} saveLabel="Crear" />
      </form>
    </SidePanel>
  );
}

// -----------------------------------------------------------------------------
// Side panel + bits reutilizados
// -----------------------------------------------------------------------------

function SidePanel({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-md bg-white shadow-xl overflow-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PanelFooter({
  onCancel,
  saving,
  saveLabel,
}: {
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
}) {
  return (
    <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
      >
        Cancelar
      </button>
      <button
        type="submit"
        disabled={saving}
        className="px-4 py-1.5 text-sm bg-vida-600 text-white rounded-lg hover:bg-vida-700 disabled:opacity-50"
      >
        {saving ? 'Guardando…' : saveLabel}
      </button>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
